import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { ARRAY_EMPTY } from '@libs/constants';
import { usePageForgotten } from '@libs/hooks/usePageForgotten';

import { useAnonymousSecret } from '@features/auth/hooks/useAnonymousSecret';
import { useAuth } from '@features/auth/hooks/useAuth';
import { useAnonymousGameToken } from '@features/game/hooks/useAnonymousGameToken';
import { IPoolInfoDto, IPoolType } from '@features/game/types/fast-game';
import {
  useFastGameSessionQuery,
  useFastGamesSocket,
  useJoinPoolsMutation,
  useKeepAliveMutation,
  usePoolsQuery,
  useRejectGameMutation,
  useRematchMutation,
  useSelectedPoolsCache,
} from '@features/shared/api/fastgames';
import { useDeviceId } from '@features/shared/hooks/useDeviceId';

import { FastGameContext } from './FastGameContext';
import { IFastGameContext } from './types';

const KEEP_ALIVE_INTERVAL = 2 * 60 * 1000;
const PAGE_FORGOTTEN_TIMEOUT = 10 * 60 * 1000;

export function FastGameProvider({ children }: { children: ReactNode }) {
  const deviceId = useDeviceId();
  const { isAuthenticated, user } = useAuth();
  const anonymousSecret = useAnonymousSecret(isAuthenticated);

  // ---- Данные ----

  const poolsQuery = usePoolsQuery();
  const sessionQuery = useFastGameSessionQuery(deviceId, anonymousSecret);
  const currentPools =
    useSelectedPoolsCache(deviceId, anonymousSecret) ?? (ARRAY_EMPTY as IPoolType[]);
  const currentGameId = sessionQuery.data?.lastEntry.g;
  const currentGameState = sessionQuery.data?.lastEntry.gst;
  const rematchExpiry = sessionQuery.data?.gameRematchExpiryMs;
  const rematchSide = sessionQuery.data?.lastEntry.grs;
  const currentGamePools = currentGameId ? sessionQuery.data?.lastEntry.ps : null;

  const anonymousId = sessionQuery.data?.anonymousId
    ? sessionQuery.data.anonymousId.startsWith('anon:')
      ? sessionQuery.data.anonymousId
      : `anon:${sessionQuery.data.anonymousId}`
    : null;

  const {
    customAccessToken,
    isPending: isTokenPending,
    isError: isTokenError,
  } = useAnonymousGameToken(currentGameId, deviceId, anonymousSecret);

  const canResetPoolsRef = useRef(false);
  canResetPoolsRef.current = Boolean(currentPools.length > 0 || rematchSide);

  // ---- Действия ----

  const joinMutation = useJoinPoolsMutation(deviceId, anonymousSecret);
  const keepAliveMutation = useKeepAliveMutation(deviceId, anonymousSecret);
  const rejectMutation = useRejectGameMutation(deviceId, anonymousSecret);
  const rematchMutation = useRematchMutation(deviceId, anonymousSecret);

  const joinPools = joinMutation.mutate;
  const resetPools = useCallback(() => {
    if (canResetPoolsRef.current) joinPools([]);
  }, [joinPools]);

  // ---- Сокет ----

  const pageForgotten = usePageForgotten(PAGE_FORGOTTEN_TIMEOUT);

  useFastGamesSocket({
    deviceId,
    anonymousId,
    anonymousSecret,
    enabled: Boolean(sessionQuery.data && (!pageForgotten || currentGameState === 'InProgress')),
  });

  // ---- Эффекты ----

  // Keep alive для ожидания в пуле
  const isQueueing = sessionQuery.data?.lastEntry.st === 'Queuing';
  useEffect(() => {
    if (!isQueueing) return;

    let timeout: ReturnType<typeof setTimeout>;

    function schedule() {
      timeout = setTimeout(async () => {
        if (!document.hidden) {
          await keepAliveMutation.mutateAsync({});
        }
        schedule();
      }, KEEP_ALIVE_INTERVAL);
    }

    schedule();

    return () => {
      clearTimeout(timeout);
    };
  }, [keepAliveMutation, isQueueing, user?.userId, anonymousSecret]);

  // выход из пулов при забытой вкладке
  useEffect(() => {
    if (pageForgotten) resetPools();
  }, [resetPools, pageForgotten]);

  // ---- Стор ----

  const pools = useMemo<IPoolInfoDto[]>(() => {
    if (!poolsQuery.data) return [];
    return Object.entries(poolsQuery.data.counters).map(([key, val]) => {
      return { pool: key as IPoolType, players: val } satisfies IPoolInfoDto;
    });
  }, [poolsQuery.data]);

  const game = useMemo(
    (): IFastGameContext['game'] | undefined =>
      currentGameId
        ? {
            gameId: currentGameId,
            isLoading: isTokenPending,
            isFinished: currentGameState !== 'InProgress',
            customToken: customAccessToken,
            customTokenError: isTokenError,
            usedPools: currentGamePools ?? ARRAY_EMPTY,
            rematchExpiration: rematchExpiry ?? undefined,
            rematchWhite: rematchSide === 'White',
            rematchBlack: rematchSide === 'Black',
            reject: rejectMutation.mutate,
            rematch: rematchMutation.mutate,
          }
        : undefined,
    [
      currentGameId,
      isTokenPending,
      currentGameState,
      customAccessToken,
      isTokenError,
      currentGamePools,
      rematchExpiry,
      rematchSide,
      rejectMutation.mutate,
      rematchMutation.mutate,
    ],
  );
  const lastGameRef = useRef(game);
  if (game) {
    lastGameRef.current = game;
  } else if (lastGameRef.current) {
    lastGameRef.current = {
      ...lastGameRef.current,
      isFinished: true,
      rematchExpiration: undefined,
      rematchWhite: false,
      rematchBlack: false,
    };
  }

  // Редирект на страницу игры
  const navigate = useNavigate();
  useEffect(() => {
    if (game && !game.isFinished) {
      navigate({
        to: '/game/pairing/$gameId',
        params: { gameId: game.gameId },
        replace: true,
      });
    }
  }, [game, navigate]);

  const store = useMemo<IFastGameContext>(
    () => ({
      isLoading: sessionQuery.isPending,
      selected: currentPools,
      pools: pools,
      playerId: user?.userId ?? anonymousId!,
      game: game ?? lastGameRef.current,

      select: joinPools,
      reset: resetPools,
    }),
    [
      currentPools,
      game,
      joinPools,
      resetPools,
      pools,
      sessionQuery.isPending,
      user?.userId,
      anonymousId,
    ],
  );
  store.pools = pools;
  store.selected = currentPools;

  return <FastGameContext.Provider value={store}>{children}</FastGameContext.Provider>;
}
